/**
 * Planning — la semaine lue depuis l'INTERVENTION UNIQUE (lot U1c, ADR-0016).
 *
 * Mise en page deux colonnes (comme Le Parc) : la grille à gauche, la colonne de droite (« À affecter » au-dessus, le briefing
 * dessous), chacune défile seule. Une case par jour travaillé. L'état (semaine, intervention lue) est porté par l'URL.
 *
 * On y écrit : créer une intervention (volet, un seul), la déplacer ou l'affecter en glissant un bloc — ou une ligne de la
 * liste « À affecter » — sur une case, l'annuler depuis son briefing. Un déplacement est une suite d'écritures jour par jour
 * (`etapesDeDeplacement`) ; un refus (409 d'une modification concurrente) relit l'écran PUIS se dit. L'écran n'invente ni
 * retard ni urgence : le serveur les calcule.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type CaseSemaine, type CreationIntervention, type CreneauJour, type InterventionPlanning, type Tournee, type ListeAAffecterInterventions, type SemaineInterventions } from '../api/client';
import { useDroitsPlanning } from '../auth/useDroits';
import { BriefingInterventionCarte } from '../composants/BriefingIntervention';
import { GestionAbsences } from '../composants/GestionAbsences';
import { GestionTechniciens } from '../composants/GestionTechniciens';
import { LegendeEtats } from '../composants/LegendeEtats';
import { PlanningInterventionsGrille } from '../composants/PlanningInterventionsGrille';
import { CalendrierPlanning } from '../composants/CalendrierPlanning';
import { RecherchePlanning } from '../composants/RecherchePlanning';
import { VoletTournee } from '../composants/VoletTournee';
import { VoletIntervention } from '../composants/VoletIntervention';
import { aujourdhuiLocal } from '../domaine/interventions';
import {
  echeanceTexte,
  formulaireDepuisCase,
  formulaireDepuisMachine,
  libelleSemaine,
  machineChoisie,
  type FormulaireVisite,
} from '../domaine/planning';
import { lireDepuisSuite } from '../domaine/planning-tournees';
import { debutDePeriode, libelleMois, majusculeInitiale, periodeVoisine, repereDeBascule, type VuePlanning } from '../domaine/planning-calendrier';
import { absenceDuJour, avertissementAbsence } from '../domaine/planning-absences';
import { PHRASE_RIEN_A_AFFECTER, basculePossible, classeMiseEnPage, etatAAffecter, libelleBascule } from '../domaine/lignes-intervention';
import { lienFiche } from '../domaine/planning-pieces';
import {
  adresseVersResultat,
  etapesDeDeplacement,
  etapesDepuisListe,
  executerEtapes,
  rangementDansLaCase,
  libelleMachine,
  lireParametresInterventions,
  numeroAffiche,
  peutDeplacer,
  phraseDeplacementIntervention,
  titreIntervention,
  type CibleCase,
  type EtapeDeplacement,
} from '../domaine/planning-interventions';

const A_AFFECTER_AFFICHEES = 50;

/** Ce qu'on a pris à la souris : un bloc de la grille, ou une intervention sans jour de la liste de droite. */
type Prise = { readonly de: 'GRILLE'; readonly bloc: CaseSemaine } | { readonly de: 'LISTE'; readonly intervention: InterventionPlanning };

export function PlanningInterventionsPage() {
  const [parametres, setParametres] = useSearchParams();
  const { semaine: jourDemande, intervention: interventionId, annulees, vue } = lireParametresInterventions(parametres);
  const droits = useDroitsPlanning();
  const [donnees, setDonnees] = useState<SemaineInterventions | null>(null);
  const [liste, setListe] = useState<ListeAAffecterInterventions | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [rechargement, setRechargement] = useState(0);
  const [volet, setVolet] = useState<{ depart?: FormulaireVisite; followUpId?: string } | null>(null);
  const [voletTournee, setVoletTournee] = useState(false);
  const [techniciensOuverts, setTechniciensOuverts] = useState(false);
  const [absencesOuvertes, setAbsencesOuvertes] = useState(false);
  const [succes, setSucces] = useState<string | null>(null);
  const [prise, setPrise] = useState<Prise | null>(null);
  const [depotEnCours, setDepotEnCours] = useState(false);
  /** Le planning reprend sa largeur sans fermer l'intervention : le détail redevient la colonne étroite. */
  const [agrandi, setAgrandi] = useState(false);
  /** Le refus d'un geste. À part de `erreur` (la lecture de la page) : la relecture qui suit un refus ne doit pas l'effacer. */
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);

  const majUrl = useCallback(
    (changement: Record<string, string | null>) => {
      const p = new URLSearchParams(parametres);
      for (const [cle, valeur] of Object.entries(changement)) {
        if (valeur === null || valeur === '') p.delete(cle);
        else p.set(cle, valeur);
      }
      setParametres(p, { replace: true });
    },
    [parametres, setParametres],
  );

  useEffect(() => {
    let annule = false;
    Promise.all([api.planningSemaineInterventions(jourDemande ?? undefined, annulees, vue), api.planningAAffecterInterventions(A_AFFECTER_AFFICHEES)])
      .then(([s, l]) => {
        if (annule) return;
        setDonnees(s);
        setListe(l);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement du planning impossible.');
      });
    return () => {
      annule = true;
    };
  }, [jourDemande, annulees, vue, rechargement]);

  const relire = useCallback(() => setRechargement((n) => n + 1), []);

  // « Convertir en intervention » depuis une suite à donner : l'adresse porte la suite, la machine et la nature ; le volet s'ouvre
  // prérempli (rien n'est écrit avant « Créer l'intervention ») et l'adresse est nettoyée pour qu'un rechargement ne le rouvre pas.
  // Les dépendances de l'effet sont des TEXTES, jamais un objet relu à chaque rendu (il relancerait l'effet en boucle).
  const depuisSuiteLu = lireDepuisSuite(parametres);
  const suiteId = depuisSuiteLu?.suiteId ?? null;
  const suiteMachineId = depuisSuiteLu?.machineId ?? null;
  const suiteNatureId = depuisSuiteLu?.natureId ?? null;
  const suiteTraitee = useRef<string | null>(null);
  const majUrlRef = useRef(majUrl);
  majUrlRef.current = majUrl;
  useEffect(() => {
    if (suiteId === null || suiteMachineId === null) return;
    if (suiteTraitee.current === suiteId) return;
    suiteTraitee.current = suiteId;
    api
      .asset(suiteMachineId)
      .then((a) => {
        const m = machineChoisie(a);
        if (m.siteId === null) {
          setErreurGeste('Cette machine n’a pas de site : rattachez-la d’abord à un site dans Le Parc avant de planifier une intervention.');
        } else {
          setVolet({ depart: { ...formulaireDepuisMachine(m, aujourdhuiLocal()), natureId: suiteNatureId ?? '' }, followUpId: suiteId });
        }
        majUrlRef.current({ depuisSuite: null, machine: null, nature: null });
      })
      .catch((e: unknown) => {
        setErreurGeste(e instanceof ApiError ? e.message : 'Machine introuvable : l’intervention n’a pas pu être préparée.');
        majUrlRef.current({ depuisSuite: null, machine: null, nature: null });
      });
  }, [suiteId, suiteMachineId, suiteNatureId]);

  // Le premier jour de la période affichée : le lundi de la semaine, ou le 1er du mois. Tant que la réponse n'est pas celle de la
  // vue demandée (bascule Semaine / Mois en cours), on le déduit de l'adresse.
  const lundi = donnees && (donnees.vue ?? 'semaine') === vue ? donnees.from : jourDemande ? debutDePeriode(vue, jourDemande) : null;
  const changerPeriode = (sens: -1 | 1) => {
    if (lundi) majUrl({ semaine: periodeVoisine(vue, lundi, sens) });
  };
  const changerVue = (nouvelle: VuePlanning) => {
    if (nouvelle === vue) return;
    // La nouvelle période contient aujourd'hui s'il est dans la période affichée, sinon le début de l'ancienne (le mois de la
    // semaine, ou la première semaine du mois).
    const repere = repereDeBascule(lundi, donnees && (donnees.vue ?? 'semaine') === vue ? donnees.to : null, donnees?.today ?? aujourdhuiLocal());
    majUrl({ vue: nouvelle === 'mois' ? 'mois' : null, semaine: debutDePeriode(nouvelle, repere) });
  };
  const libellePeriode = lundi === null ? 'Chargement…' : vue === 'mois' ? majusculeInitiale(libelleMois(lundi)) : libelleSemaine(lundi);
  const selectionner = (id: string) => majUrl({ intervention: id });
  const compteurs = donnees?.compteurs;

  const appels = {
    ajouterJour: api.ajouterJourIntervention,
    retirerJour: api.retirerJourIntervention,
    ajouterMembre: api.ajouterMembreIntervention,
    retirerMembre: api.retirerMembreIntervention,
    definirChef: api.definirChefIntervention,
    definirCreneau: (id: string, revision: number, jour: string, slot: CreneauJour) => api.definirCreneauIntervention(id, revision, jour, { slot }),
  };

  /** Joue les étapes ; un refus relit l'écran PUIS se dit (le message n'est pas effacé par la relecture). */
  const jouer = async (interventionVisee: InterventionPlanning, etapes: readonly EtapeDeplacement[], reussite: string) => {
    setDepotEnCours(true);
    setErreurGeste(null);
    setSucces(null);
    try {
      await executerEtapes(interventionVisee.id, interventionVisee.revision, etapes, appels);
      setSucces(reussite);
      relire();
    } catch (e) {
      relire();
      setErreurGeste(
        e instanceof ApiError && e.status === 409
          ? `${e.message} L’écran vient d’être relu : revoyez l’intervention avant de recommencer.`
          : e instanceof ApiError || e instanceof Error
            ? e.message
            : 'Déplacement impossible.',
      );
    } finally {
      setDepotEnCours(false);
    }
  };

  const nomDe = (technicianId: string | null) => donnees?.technicians.find((t) => t.id === technicianId)?.name ?? null;

  /** Une absence n'interdit rien : on avertit, et on laisse choisir (il peut y avoir une bonne raison). */
  const absenceConfirmee = (cible: CibleCase): boolean => {
    const nom = nomDe(cible.technicianId);
    if (cible.technicianId === null || nom === null) return true;
    const mise = avertissementAbsence(absenceDuJour(donnees?.absences ?? [], cible.technicianId, cible.jour), nom, cible.jour);
    return mise === null || window.confirm(mise);
  };

  const deposer = async (cible: CibleCase) => {
    const p = prise;
    setPrise(null);
    if (p === null || donnees === null || depotEnCours) return;
    if (p.de === 'LISTE') {
      if (!absenceConfirmee(cible)) return;
      await jouer(p.intervention, etapesDepuisListe(cible), phraseDeplacementIntervention(cible, nomDe(cible.technicianId)));
      return;
    }
    const resultat = etapesDeDeplacement(donnees.cases, p.bloc, cible);
    if ('refus' in resultat) {
      setSucces(null);
      setErreurGeste(resultat.refus);
      return;
    }
    if (resultat.etapes.length === 0) return;
    if (!absenceConfirmee(cible)) return;
    await jouer(p.bloc.intervention, resultat.etapes, phraseDeplacementIntervention(cible, nomDe(cible.technicianId)));
  };

  /** Monter ou descendre une intervention dans la journée : la liste complète de la case, rangée par le serveur en une fois. */
  const ranger = async (casesDeLaCase: readonly CaseSemaine[], id: string, sens: -1 | 1) => {
    if (depotEnCours) return;
    const plan = rangementDansLaCase(casesDeLaCase, id, sens);
    if ('refus' in plan) {
      setSucces(null);
      setErreurGeste(plan.refus);
      return;
    }
    const jour = casesDeLaCase[0]?.day;
    if (jour === undefined) return;
    setDepotEnCours(true);
    setErreurGeste(null);
    setSucces(null);
    try {
      await api.ordonnerJournee(jour, plan.items);
      setSucces(sens < 0 ? 'Intervention avancée dans la journée.' : 'Intervention reculée dans la journée.');
      relire();
    } catch (e) {
      relire();
      setErreurGeste(
        e instanceof ApiError && e.status === 409
          ? `${e.message} L’écran vient d’être relu : revoyez la journée avant de recommencer.`
          : e instanceof ApiError || e instanceof Error
            ? e.message
            : 'Rangement impossible.',
      );
    } finally {
      setDepotEnCours(false);
    }
  };

  const glisser = {
    enCours: prise !== null,
    onGlisser: (bloc: CaseSemaine) => setPrise({ de: 'GRILLE', bloc }),
    onFinGlisser: () => setPrise(null),
    onDeposer: (cible: CibleCase) => void deposer(cible),
  };

  /** Une ligne de la liste « À affecter » se prend comme un bloc : sans jour, elle reçoit le sien en tombant sur une case. */
  const debutGlisserListe = (e: DragEvent<HTMLDivElement>, i: InterventionPlanning) => {
    e.dataTransfer.setData('text/plain', i.id);
    e.dataTransfer.effectAllowed = 'move';
    setPrise({ de: 'LISTE', intervention: i });
  };

  const ouvrirVolet = (depart?: FormulaireVisite) => {
    setSucces(null);
    setErreurGeste(null);
    setVolet(depart ? { depart } : {});
  };

  const apresCreation = (c: CreationIntervention) => {
    setVolet(null);
    const n = c.interventions.length;
    setSucces(n === 1 ? 'Intervention créée.' : `${n} interventions créées dans un même passage.`);
    const premiere = c.interventions[0];
    if (premiere) majUrl({ intervention: premiere.id });
    relire();
  };

  /** Une tournée vient d'être créée : on montre la semaine de sa première occurrence et on ouvre son intervention. */
  const apresCreationTournee = (t: Tournee) => {
    setVoletTournee(false);
    const n = t.occurrences.length;
    setSucces(`Tournée créée : ${t.label} — ${n === 1 ? '1 occurrence créée' : `${n} occurrences créées`}, une intervention par machine.`);
    const premiere = t.occurrences[0];
    if (premiere) majUrl({ semaine: premiere.plannedDate ? debutDePeriode(vue, premiere.plannedDate) : null, intervention: premiere.interventionId });
    relire();
  };

  return (
    <div className="gammes-page planning-page">
      <div className="entete-page">
        <div>
          <h1>Planning des interventions</h1>
          <p>
            La semaine, une case par jour travaillé : qui va où, quand, et ce qu’il faut prévoir.
            {droits.peutGererPlanning &&
              ' Glissez un bloc sur une autre case pour changer son jour ou son technicien ; déposez-le en haut (matin) ou en bas (après-midi) de la case.'}
          </p>
        </div>
        <div className="saisie-ligne">
          {droits.peutLirePlanning && (
            // La fiche de préparation du jour : les pièces à sortir du magasin, lues dans les interventions du jour.
            <Link className="bouton-lien secondaire-lien" to={lienFiche(donnees?.today ?? aujourdhuiLocal())}>
              Fiche de préparation
            </Link>
          )}
          {droits.peutLirePlanning && (
            <Link className="bouton-lien secondaire-lien" to="/planning/annee">
              Préparer l’année
            </Link>
          )}
          {droits.peutLirePlanning && (
            <button type="button" className="secondaire" onClick={() => setTechniciensOuverts(true)}>
              Techniciens
            </button>
          )}
          {droits.peutLirePlanning && (
            <button type="button" className="secondaire" onClick={() => setAbsencesOuvertes(true)}>
              Absences
            </button>
          )}
          {droits.peutGererPlanning && (
            <button type="button" className="secondaire" onClick={() => setVoletTournee(true)}>
              + Nouvelle tournée
            </button>
          )}
          {droits.peutGererPlanning && (
            <button type="button" onClick={() => ouvrirVolet()}>
              + Nouvelle intervention
            </button>
          )}
        </div>
      </div>

      {!droits.peutGererPlanning && <div className="message info">Lecture seule : vous pouvez consulter le planning, pas le modifier.</div>}
      {succes && <div className="message succes">{succes}</div>}
      {erreurGeste && <div className="message erreur">{erreurGeste}</div>}

      <div className="planning-barre">
        <div className="saisie-ligne">
          <button type="button" className="secondaire" aria-label={vue === 'mois' ? 'Mois précédent' : 'Semaine précédente'} disabled={!lundi} onClick={() => changerPeriode(-1)}>
            ‹
          </button>
          <button type="button" className="secondaire" disabled={!lundi} onClick={() => majUrl({ semaine: debutDePeriode(vue, donnees?.today ?? aujourdhuiLocal()) })}>
            Aujourd’hui
          </button>
          <button type="button" className="secondaire" aria-label={vue === 'mois' ? 'Mois suivant' : 'Semaine suivante'} disabled={!lundi} onClick={() => changerPeriode(1)}>
            ›
          </button>
          <CalendrierPlanning
            vue={vue}
            debut={lundi}
            fin={donnees && (donnees.vue ?? 'semaine') === vue ? donnees.to : null}
            aujourdhui={donnees?.today ?? aujourdhuiLocal()}
            libelle={libellePeriode}
            onAller={(jour) => majUrl({ semaine: debutDePeriode(vue, jour) })}
          />
          <span className="planning-vues" role="group" aria-label="Période affichée">
            <button type="button" className={`secondaire discret${vue === 'semaine' ? ' actif' : ''}`} aria-pressed={vue === 'semaine'} onClick={() => changerVue('semaine')}>
              Semaine
            </button>
            <button type="button" className={`secondaire discret${vue === 'mois' ? ' actif' : ''}`} aria-pressed={vue === 'mois'} onClick={() => changerVue('mois')}>
              Mois
            </button>
          </span>
          <label className="planning-annulees">
            <input type="checkbox" checked={annulees} onChange={(e) => majUrl({ annulees: e.target.checked ? 'oui' : null })} /> Afficher les annulées
          </label>
          <RecherchePlanning onChoisir={(b) => majUrl(adresseVersResultat(b, vue))} />
        </div>
        <div className="planning-compteurs">
          {basculePossible(interventionId !== null) && (
            <button type="button" className="secondaire discret" aria-pressed={agrandi} onClick={() => setAgrandi((a) => !a)}>
              {libelleBascule(agrandi)}
            </button>
          )}
          {compteurs && (
            <>
              <span title="Jours travaillés sans équipe, et interventions sans aucun jour">
                <strong>{compteurs.aAffecter}</strong> à affecter
              </span>
              <span className={compteurs.enRetard > 0 ? 'planning-alerte' : ''} title="Cases dont le jour est passé sans que l’intervention soit faite">
                <strong>{compteurs.enRetard}</strong> en retard
              </span>
            </>
          )}
        </div>
      </div>

      <LegendeEtats />

      {erreur && <div className="message erreur">{erreur}</div>}

      <div className={classeMiseEnPage(interventionId !== null, agrandi)}>
        <div className="carte planning-zone-grille">
          {!donnees && !erreur && <div className="vide">Chargement…</div>}
          {donnees && (
            <PlanningInterventionsGrille
              semaine={donnees}
              selection={interventionId}
              peutGerer={droits.peutGererPlanning}
              glisser={glisser}
              onRanger={(cases, id, sens) => void ranger(cases, id, sens)}
              onSelectionner={selectionner}
              onNouvelle={(jour, technicienId) => ouvrirVolet(formulaireDepuisCase(jour, technicienId))}
            />
          )}
        </div>

        <div className="planning-droite">
          {etatAAffecter(liste) === 'VIDE' && <div className="carte planning-a-affecter-vide">{PHRASE_RIEN_A_AFFECTER}</div>}
          {etatAAffecter(liste) !== 'VIDE' && (
          <div className="carte planning-a-affecter">
            <h2>À affecter</h2>
            <p className="aide">
              Les interventions sans aucun jour travaillé.
              {liste && liste.total > 0 && ` ${liste.total} au total.`}
            </p>
            {liste === null && !erreur && <div className="vide">Chargement…</div>}
            {liste !== null && liste.items.length > 0 && (
              <ul className="parc-lignes">
                {liste.items.map(({ intervention: i, joursRestants, enRetard, urgence }) => {
                  const glissable = peutDeplacer(i, droits.peutGererPlanning);
                  // Un div role="button", pas un <button> : Firefox ne démarre pas un glisser sur un bouton.
                  const surTouche = (e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectionner(i.id);
                    }
                  };
                  return (
                    <li key={i.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        className={`parc-ligne${i.id === interventionId ? ' actif' : ''}${glissable ? ' glissable' : ''}`}
                        aria-pressed={i.id === interventionId}
                        draggable={glissable || undefined}
                        onClick={() => selectionner(i.id)}
                        onKeyDown={surTouche}
                        onDragStart={glissable ? (e) => debutGlisserListe(e, i) : undefined}
                        onDragEnd={glissable ? () => setPrise(null) : undefined}
                      >
                        <div className="parc-ligne-titre">
                          <strong>{titreIntervention(i)}</strong>
                          {urgence !== null || enRetard ? (
                            <span className={`etat ${enRetard || urgence === 'ROUGE' ? 'revoque' : 'attente'}`}>{echeanceTexte(joursRestants)}</span>
                          ) : (
                            joursRestants !== null && <span className="aide">{echeanceTexte(joursRestants)}</span>
                          )}
                        </div>
                        <div className="aide">{[i.site?.name, i.natureLabel, libelleMachine(i)].filter(Boolean).join(' · ')}</div>
                        <div className="aide">{numeroAffiche(i)}</div>
                      </div>
                    </li>
                  );
                })}
                {liste.total > liste.items.length && (
                  <li className="parc-charger-plus">
                    <p className="aide">{liste.total - liste.items.length} autres interventions sans jour : traitez celles-ci d’abord, la liste se raccourcit.</p>
                  </li>
                )}
              </ul>
            )}
          </div>
          )}

          <div className="planning-briefing">
            {interventionId ? (
              <BriefingInterventionCarte
                key={interventionId}
                interventionId={interventionId}
                rechargement={rechargement}
                peutGerer={droits.peutGererPlanning}
                onChange={relire}
                onMessage={setSucces}
              />
            ) : (
              <div className="carte">
                <div className="vide">Choisissez une intervention dans la grille ou dans la liste pour lire son détail.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {voletTournee && <VoletTournee onCreee={apresCreationTournee} onFermer={() => setVoletTournee(false)} />}
      {techniciensOuverts && (
        <GestionTechniciens peutGerer={droits.peutGererPlanning} onChange={relire} onFermer={() => setTechniciensOuverts(false)} />
      )}
      {absencesOuvertes && donnees && (
        <GestionAbsences
          peutGerer={droits.peutGererPlanning}
          techniciens={(donnees.technicians ?? []).filter((t) => !t.archived)}
          aujourdhui={donnees.today}
          dayMinutes={donnees.dayMinutes}
          onChange={relire}
          onFermer={() => setAbsencesOuvertes(false)}
        />
      )}
      {volet && (
        <VoletIntervention
          {...(volet.depart ? { depart: volet.depart } : {})}
          {...(volet.followUpId ? { followUpId: volet.followUpId } : {})}
          onCreeeIntervention={apresCreation}
          onFermer={() => setVolet(null)}
        />
      )}
    </div>
  );
}
